import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import api from "../lib/api";
import { getStoredUser } from "../lib/auth";
import { useLanguage } from "../lib/language";
import { repairMojibakeDeep } from "../lib/text";

const emptyCourseForm = { title: "", description: "" };
const emptyAssessmentForm = {
  title: "",
  type: "pre",
  show_results_to_student: false,
  show_answers_to_student: false,
  show_ai_report_to_student: false
};
const emptyQuestionForm = {
  question_text: "",
  question_type: "essay",
  points: 1,
  question_order: 1,
  options: ["", ""]
};

function TrainerResultsPage() {
  const user = getStoredUser();
  const { language } = useLanguage();
  const isArabic = language === "ar";
  const loginMessage = isArabic ? "سجل الدخول بحساب مدرب أولًا." : "Please log in with a trainer account first.";
  const contentSearchLabel = isArabic ? "بحث سريع" : "Quick Search";
  const contentSearchPlaceholder = isArabic ? "ابحث في الكورسات أو التقييمات أو الأسئلة" : "Search courses, assessments, or questions";
  const bankSearchPlaceholder = isArabic ? "ابحث داخل بنك الأسئلة" : "Search question bank";
  const allTypesLabel = isArabic ? "كل الأنواع" : "All Types";
  const noSearchResults = isArabic ? "لا توجد نتائج مطابقة للبحث الحالي." : "No results match the current search.";
  const rawTranslations = isArabic
    ? {
        navEyebrow: "تنقل المدرب",
        navTitle: "تنقل أسرع داخل لوحة المدرب",
        managementTab: "إدارة المحتوى",
        resultsTab: "النتائج والتحليلات",
        trainerWorkspace: "مساحة عمل المدرب",
        contentManagement: "إدارة المحتوى التعليمي",
        contentTreeEyebrow: "شجرة المحتوى",
        contentTreeTitle: "الكورسات والتقييمات والأسئلة",
        learnerResultsEyebrow: "نتائج المتعلمين",
        learnerResultsTitle: "الطلاب والإجابات",
        dashboardEyebrow: "لوحة المتابعة",
        dashboardTitle: "الرسوم والمقارنات",
        courses: "الكورسات",
        assessments: "التقييمات",
        questions: "الأسئلة",
        loadingCourses: "جار تحميل الكورسات...",
        loadingAssessments: "جار تحميل التقييمات...",
        loadingQuestions: "جار تحميل الأسئلة...",
        loadingSubmissions: "جار تحميل التسليمات...",
        noCourses: "لا توجد كورسات بعد. أنشئ أول كورس من اللوحة الجانبية.",
        noAssessments: "لا توجد تقييمات لهذا الكورس حتى الآن.",
        noQuestions: "لا توجد أسئلة في هذا التقييم حتى الآن.",
        noSubmissions: "لا توجد تسليمات لهذا التقييم حتى الآن.",
        jumpTo: "انتقال سريع",
        courseForm: "بيانات الكورس",
        assessmentForm: "بيانات التقييم",
        questionForm: "بيانات السؤال",
        contentList: "قائمة المحتوى",
        submissionsSection: "التسليمات",
        analyticsSection: "التحليلات",
        chooseCourseToSeeAssessments: "اختر كورسًا بالأعلى لعرض تقييماته.",
        chooseAssessmentToSeeQuestions: "اختر تقييمًا لعرض أسئلته.",
        chooseAssessmentFromManagement: "اختر تقييمًا أولًا من تبويب إدارة المحتوى.",
        assessmentsCount: "تقييم",
        submissionsCount: "تسليم",
        codeLabel: "الكود",
        edit: "تعديل",
        delete: "حذف",
        copyCode: "نسخ الكود",
        cancelEdit: "إلغاء التعديل",
        courseTitle: "عنوان الكورس",
        courseDescription: "وصف الكورس",
        createCourse: "إنشاء كورس جديد",
        editCourse: "تعديل الكورس",
        createCourseButton: "إنشاء الكورس",
        saveCourseButton: "حفظ الكورس",
        createAssessment: "إنشاء تقييم جديد",
        editAssessment: "تعديل التقييم",
        currentCourse: "الكورس الحالي",
        selectCourseFirst: "اختر كورسًا من القائمة أولًا",
        assessmentTitle: "عنوان التقييم",
        assessmentHint: "سيتم إنشاء نسختين تلقائيًا: قبلي وبعدي بنفس الكود الأساسي.",
        allowStudentSeeScore: "السماح للطالب برؤية الدرجة",
        allowStudentSeeAnswers: "السماح للطالب بمراجعة الإجابات السابقة",
        allowStudentSeeAi: "السماح للطالب برؤية تقرير الذكاء الاصطناعي",
        createAssessmentButton: "إنشاء التقييم القبلي والبعدي",
        saveAssessmentButton: "حفظ إعدادات التقييم",
        createQuestion: "إضافة سؤال جديد",
        editQuestion: "تعديل السؤال",
        currentAssessment: "التقييم الحالي",
        selectAssessmentFirst: "اختر تقييمًا من القائمة أولًا",
        questionText: "نص السؤال",
        questionTextPlaceholder: "اكتب السؤال هنا",
        questionType: "نوع السؤال",
        essay: "مقالي",
        mcq: "اختيار من متعدد",
        trueFalse: "صح / خطأ",
        rating: "تقييم رقمي",
        questionPoints: "درجة السؤال",
        questionPointsPlaceholder: "مثال: 1 أو 5",
        questionPointsHint: "هذه هي الدرجة التي يحصل عليها الطالب في هذا السؤال.",
        questionOrder: "ترتيب السؤال",
        questionOrderPlaceholder: "مثال: 1 لأول سؤال",
        questionOrderHint: "هذا يتحكم في مكان ظهور السؤال داخل الاختبار.",
        addOption: "إضافة اختيار",
        createQuestionButton: "إنشاء السؤال",
        saveQuestionButton: "حفظ السؤال",
        optionLabel: "اختيار",
        correctAnswer: "الإجابة الصحيحة",
        pre: "قبلي",
        post: "بعدي",
        points: "درجة",
        order: "ترتيب",
        options: "الاختيارات",
        submissions: "التسليمات",
        exportCsv: "تصدير CSV",
        exportSubmissions: "تصدير التسليمات",
        exportCourseResults: "تصدير نتائج الكورس",
        exportAiCourseReport: "تصدير تقرير الكورس",
        exportAiAssessmentReport: "تصدير تقرير التقييم",
        copyCourseReport: "نسخ تقرير الكورس",
        copyAssessmentReport: "نسخ تقرير التقييم",
        submissionSort: "ترتيب التسليمات",
        courseResultSort: "ترتيب نتائج الكورس",
        latestFirst: "الأحدث أولًا",
        highestScore: "الأعلى درجة",
        mostAnswers: "الأكثر إجابات",
        nameSort: "الاسم",
        bestImprovement: "الأعلى تحسنًا",
        highestPost: "الأعلى بعدي",
        score: "الدرجة",
        answersCount: "إجابة",
        studentDetails: "بيانات الطالب",
        aiSubmissionSummary: "ملخص الذكاء الاصطناعي للتسليم",
        noAiSummary: "لا يوجد ملخص ذكاء اصطناعي بعد.",
        copyEmail: "نسخ البريد الإلكتروني",
        name: "الاسم",
        governorate: "المحافظة",
        educationLevel: "المستوى التعليمي",
        noWrittenAnswer: "لا توجد إجابة نصية",
        noSelectedOption: "لا يوجد اختيار محدد",
        sentiment: "المشاعر",
        level: "المستوى",
        courseSummary: "ملخص الكورس",
        totalStudents: "عدد الطلاب",
        totalSubmissions: "عدد التسليمات",
        averageScore: "متوسط الدرجات",
        improvedStudents: "الطلاب الذين تحسنوا بعد المقارنة",
        aiCourseReport: "تقرير الذكاء الاصطناعي للكورس",
        strengths: "نقاط القوة",
        risks: "المخاطر",
        recommendations: "التوصيات",
        assessmentSummary: "ملخص التقييم",
        totalAiInsights: "عدد الرؤى الذكية",
        aiAssessmentReport: "تقرير الذكاء الاصطناعي للتقييم",
        dominantSentiment: "المشاعر الغالبة",
        dominantLevel: "المستوى الغالب",
        topKeywords: "أهم الكلمات المفتاحية",
        prePostComparison: "مقارنة قبلي / بعدي",
        sentimentLevels: "تحليل المشاعر والمستويات",
        positive: "إيجابي",
        neutral: "محايد",
        negative: "سلبي",
        high: "مرتفع",
        medium: "متوسط",
        low: "منخفض",
        courseStudentResults: "نتائج طلاب الكورس",
        student: "الطالب",
        improvement: "التحسن",
        copyFailed: "تعذر النسخ في هذا المتصفح",
        copiedCode: "تم نسخ كود الدخول بنجاح",
        copiedEmail: "تم نسخ بريد الطالب بنجاح",
        loadCoursesError: "تعذر تحميل الكورسات",
        loadAssessmentsError: "تعذر تحميل التقييمات",
        loadQuestionsError: "تعذر تحميل الأسئلة",
        loadSubmissionsError: "تعذر تحميل التسليمات",
        loadStudentAnswersError: "تعذر تحميل إجابات الطالب",
        loadAnalyticsError: "تعذر تحميل التحليلات",
        loadCourseResultsError: "تعذر تحميل نتائج الكورس",
        loadCourseAiError: "تعذر تحميل تقرير الذكاء الاصطناعي للكورس",
        loadAssessmentAiError: "تعذر تحميل تقرير الذكاء الاصطناعي للتقييم"
      }
    : {
        navEyebrow: "Trainer Navigation",
        navTitle: "Faster navigation inside the trainer dashboard",
        managementTab: "Content Management",
        resultsTab: "Results & Analytics",
        trainerWorkspace: "Trainer Workspace",
        contentManagement: "Learning Content Management",
        contentTreeEyebrow: "Content Tree",
        contentTreeTitle: "Courses, Assessments, and Questions",
        learnerResultsEyebrow: "Learner Results",
        learnerResultsTitle: "Students and Answers",
        dashboardEyebrow: "Dashboard",
        dashboardTitle: "Charts and Comparisons",
        courses: "Courses",
        assessments: "Assessments",
        questions: "Questions",
        loadingCourses: "Loading courses...",
        loadingAssessments: "Loading assessments...",
        loadingQuestions: "Loading questions...",
        loadingSubmissions: "Loading submissions...",
        noCourses: "No courses yet. Create your first course from the left panel.",
        noAssessments: "There are no assessments for this course yet.",
        noQuestions: "There are no questions in this assessment yet.",
        noSubmissions: "There are no submissions for this assessment yet.",
        jumpTo: "Quick Jump",
        courseForm: "Course Form",
        assessmentForm: "Assessment Form",
        questionForm: "Question Form",
        contentList: "Content List",
        submissionsSection: "Submissions",
        analyticsSection: "Analytics",
        chooseCourseToSeeAssessments: "Choose a course above to view its assessments.",
        chooseAssessmentToSeeQuestions: "Choose an assessment to view its questions.",
        chooseAssessmentFromManagement: "Choose an assessment first from the content management tab.",
        assessmentsCount: "assessments",
        submissionsCount: "submissions",
        codeLabel: "code",
        edit: "Edit",
        delete: "Delete",
        copyCode: "Copy Code",
        cancelEdit: "Cancel Edit",
        courseTitle: "Course Title",
        courseDescription: "Course Description",
        createCourse: "Create New Course",
        editCourse: "Edit Course",
        createCourseButton: "Create Course",
        saveCourseButton: "Save Course",
        createAssessment: "Create New Assessment",
        editAssessment: "Edit Assessment",
        currentCourse: "Current Course",
        selectCourseFirst: "Choose a course from the list first",
        assessmentTitle: "Assessment Title",
        assessmentHint: "Two versions will be created automatically: a pre assessment and a post assessment with the same base code.",
        allowStudentSeeScore: "Allow the student to see the score",
        allowStudentSeeAnswers: "Allow the student to review previous answers",
        allowStudentSeeAi: "Allow the student to see the AI report",
        createAssessmentButton: "Create Pre and Post Assessment",
        saveAssessmentButton: "Save Assessment Settings",
        createQuestion: "Add New Question",
        editQuestion: "Edit Question",
        currentAssessment: "Current Assessment",
        selectAssessmentFirst: "Choose an assessment from the list first",
        questionText: "Question Text",
        questionTextPlaceholder: "Write the question here",
        questionType: "Question Type",
        essay: "Essay",
        mcq: "Multiple Choice",
        trueFalse: "True / False",
        rating: "Rating",
        questionPoints: "Question Points",
        questionPointsPlaceholder: "Example: 1 or 5",
        questionPointsHint: "This is the score the student gets for this question.",
        questionOrder: "Question Order",
        questionOrderPlaceholder: "Example: 1 for the first question",
        questionOrderHint: "This controls where the question appears in the exam order.",
        addOption: "Add Option",
        createQuestionButton: "Create Question",
        saveQuestionButton: "Save Question",
        optionLabel: "Option",
        correctAnswer: "Correct answer",
        pre: "Pre",
        post: "Post",
        points: "points",
        order: "order",
        options: "Options",
        submissions: "Submissions",
        exportCsv: "Export CSV",
        exportSubmissions: "Export Submissions",
        exportCourseResults: "Export Course Results",
        exportAiCourseReport: "Export Course Report",
        exportAiAssessmentReport: "Export Assessment Report",
        copyCourseReport: "Copy Course Report",
        copyAssessmentReport: "Copy Assessment Report",
        submissionSort: "Submission Sort",
        courseResultSort: "Course Results Sort",
        latestFirst: "Latest First",
        highestScore: "Highest Score",
        mostAnswers: "Most Answers",
        nameSort: "Name",
        bestImprovement: "Best Improvement",
        highestPost: "Highest Post",
        score: "Score",
        answersCount: "answers",
        studentDetails: "Student Details",
        aiSubmissionSummary: "AI Submission Summary",
        noAiSummary: "No AI summary yet.",
        copyEmail: "Copy Email",
        name: "Name",
        governorate: "Governorate",
        educationLevel: "Education Level",
        noWrittenAnswer: "No written answer",
        noSelectedOption: "No selected option",
        sentiment: "Sentiment",
        level: "Level",
        courseSummary: "Course Summary",
        totalStudents: "Total Students",
        totalSubmissions: "Total Submissions",
        averageScore: "Average Score",
        improvedStudents: "Improved After Comparison",
        aiCourseReport: "AI Course Report",
        strengths: "Strengths",
        risks: "Risks",
        recommendations: "Recommendations",
        assessmentSummary: "Assessment Summary",
        totalAiInsights: "AI Insights",
        aiAssessmentReport: "AI Assessment Report",
        dominantSentiment: "Dominant Sentiment",
        dominantLevel: "Dominant Level",
        topKeywords: "Top Keywords",
        prePostComparison: "Pre / Post Comparison",
        sentimentLevels: "Sentiment and Levels Analysis",
        positive: "Positive",
        neutral: "Neutral",
        negative: "Negative",
        high: "High",
        medium: "Medium",
        low: "Low",
        courseStudentResults: "Course Student Results",
        student: "Student",
        improvement: "Improvement",
        copyFailed: "Copy failed on this browser",
        copiedCode: "Access code copied successfully",
        copiedEmail: "Student email copied successfully",
        loadCoursesError: "Unable to load courses",
        loadAssessmentsError: "Unable to load assessments",
        loadQuestionsError: "Unable to load questions",
        loadSubmissionsError: "Unable to load submissions",
        loadStudentAnswersError: "Unable to load student answers",
        loadAnalyticsError: "Unable to load analytics",
        loadCourseResultsError: "Unable to load course results",
        loadCourseAiError: "Unable to load course AI report",
        loadAssessmentAiError: "Unable to load assessment AI report"
      };
  const t = repairMojibakeDeep(rawTranslations);
  const rawBankText = isArabic
    ? {
        title: "بنك الأسئلة",
        hint: "احفظ أفضل أسئلتك مرة واحدة ثم أعد استخدامها في أي تقييم.",
        loading: "جار تحميل بنك الأسئلة...",
        empty: "لا توجد أسئلة محفوظة في البنك بعد.",
        save: "حفظ في البنك",
        update: "تحديث عنصر البنك",
        add: "إضافة إلى التقييم الحالي",
        addAll: "إضافة كل النتائج الحالية",
        edit: "تعديل عنصر البنك",
        saveExisting: "حفظ السؤال في البنك",
        saveHint: "احفظ مسودة السؤال الحالية مع الاختيارات داخل البنك لإعادة استخدامها.",
        chooseAssessment: "اختر تقييمًا أولًا لإضافة سؤال من البنك إليه.",
        optionsCountLabel: "اختيار",
        sortLabel: "الترتيب",
        recentFirst: "الأحدث أولًا",
        pointsHighToLow: "الأعلى درجة",
        allRequiredModes: "كل الأسئلة",
        requiredOnly: "الأسئلة المطلوبة",
        optionalOnly: "الأسئلة الاختيارية",
        saved: "تم حفظ السؤال في بنك الأسئلة بنجاح",
        updated: "تم تحديث سؤال البنك بنجاح",
        deleted: "تم حذف السؤال من بنك الأسئلة",
        added: "تمت إضافة السؤال من البنك إلى التقييم",
        bulkAdded: "تمت إضافة مجموعة أسئلة البنك الحالية إلى التقييم",
        loadError: "تعذر تحميل بنك الأسئلة",
        saveError: "تعذر حفظ السؤال في البنك",
        updateError: "تعذر تحديث سؤال البنك",
        deleteError: "تعذر حذف السؤال من البنك",
        addError: "تعذر إضافة السؤال من البنك إلى التقييم",
        bulkAddError: "تعذر إضافة النتائج الحالية من بنك الأسئلة",
        deleteTitle: "حذف من بنك الأسئلة",
        deleteMessage: "هل أنت متأكد من حذف هذا السؤال المحفوظ من بنك الأسئلة؟",
        editHint: "حمّل سؤال البنك إلى نموذج التحرير ثم حدّثه أو استخدمه داخل التقييم."
      }
    : {
        title: "Question Bank",
        hint: "Save your strongest questions once, then reuse them in any assessment.",
        loading: "Loading question bank...",
        empty: "No saved questions in the bank yet.",
        save: "Save to Bank",
        update: "Update Bank Item",
        add: "Add to Current Assessment",
        addAll: "Add All Current Results",
        edit: "Edit Bank Item",
        saveExisting: "Save Question to Bank",
        saveHint: "Save the current question draft and its options in the reusable bank.",
        chooseAssessment: "Choose an assessment first to add a bank question to it.",
        optionsCountLabel: "options",
        sortLabel: "Sort",
        recentFirst: "Newest First",
        pointsHighToLow: "Highest Points",
        allRequiredModes: "All Questions",
        requiredOnly: "Required Only",
        optionalOnly: "Optional Only",
        saved: "Question saved to the bank successfully",
        updated: "Question bank item updated successfully",
        deleted: "Question removed from the bank",
        added: "Question added from the bank to the assessment",
        bulkAdded: "The current bank results were added to the assessment",
        loadError: "Unable to load question bank",
        saveError: "Unable to save question to the bank",
        updateError: "Unable to update question bank item",
        deleteError: "Unable to delete question from the bank",
        addError: "Unable to add question from the bank to the assessment",
        bulkAddError: "Unable to add the current bank results to the assessment",
        deleteTitle: "Delete from Question Bank",
        deleteMessage: "Are you sure you want to delete this saved bank question?",
        editHint: "Load the bank question into the builder, then update it or reuse it in an assessment."
      };
  const bankText = repairMojibakeDeep(rawBankText);
  const rawPdfText = isArabic
    ? {
        exportPdf: "تصدير PDF",
        pdfReady: "التقرير جاهز للطباعة. اختر من نافذة الطباعة Save as PDF لحفظه كملف PDF.",
        pdfEmpty: "لا يوجد تقرير متاح للتصدير الآن.",
        summaryTitle: "ملخص سريع",
        summaryHint: "راجع حالة المحتوى الحالية قبل إنشاء أو تعديل أي شيء.",
        builderTitle: "منشئ المحتوى اليدوي",
        builderHint: "أنشئ أو عدل الكورسات والتقييمات والأسئلة من هنا.",
        libraryTitle: "مكتبة المحتوى الحالية",
        libraryHint: "راجع كل ما تم إنشاؤه بالفعل في مكان واحد."
      }
    : {
        exportPdf: "Export PDF",
        pdfReady: "The report is ready to print. Choose Save as PDF from the print window.",
        pdfEmpty: "There is no report to export right now.",
        summaryTitle: "Quick Summary",
        summaryHint: "See the current content status before creating or editing anything.",
        builderTitle: "Manual Content Builder",
        builderHint: "Create or edit courses, assessments, and questions from here.",
        libraryTitle: "Current Content Library",
        libraryHint: "Review everything already created in one place."
      };
  const pdfText = repairMojibakeDeep(rawPdfText);
  const [activeTab, setActiveTab] = useState("results");
  const [courses, setCourses] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [questionBank, setQuestionBank] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [courseResults, setCourseResults] = useState(null);
  const [assessmentAnalytics, setAssessmentAnalytics] = useState(null);
  const [courseAiReport, setCourseAiReport] = useState(null);
  const [assessmentAiReport, setAssessmentAiReport] = useState(null);
  const [submissionDetails, setSubmissionDetails] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState(null);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [courseForm, setCourseForm] = useState(emptyCourseForm);
  const [assessmentForm, setAssessmentForm] = useState(emptyAssessmentForm);
  const [questionForm, setQuestionForm] = useState(emptyQuestionForm);
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [editingAssessmentId, setEditingAssessmentId] = useState(null);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingBankQuestionId, setEditingBankQuestionId] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState("info");
  const [contentSearch, setContentSearch] = useState("");
  const [bankSearch, setBankSearch] = useState("");
  const [bankTypeFilter, setBankTypeFilter] = useState("all");
  const [bankRequiredFilter, setBankRequiredFilter] = useState("all");
  const [bankSort, setBankSort] = useState("recent");
  const [resultsSearch, setResultsSearch] = useState("");
  const [submissionSort, setSubmissionSort] = useState("latest");
  const [courseResultsSort, setCourseResultsSort] = useState("name");
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [isLoadingAssessments, setIsLoadingAssessments] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isLoadingQuestionBank, setIsLoadingQuestionBank] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const courseFormRef = useRef(null);
  const assessmentFormRef = useRef(null);
  const questionFormRef = useRef(null);
  const questionBankRef = useRef(null);
  const contentListRef = useRef(null);
  const submissionsRef = useRef(null);
  const analyticsRef = useRef(null);

  const selectedCourse = courses.find((course) => course.id === selectedCourseId) || null;
  const selectedAssessment =
    assessments.find((assessment) => assessment.id === selectedAssessmentId) || null;

  const questionTypeLabel = (type) =>
    ({ essay: t.essay, mcq: t.mcq, true_false: t.trueFalse, rating: t.rating }[type] || type);

  const normalizedContentSearch = contentSearch.trim().toLowerCase();
  const normalizedBankSearch = bankSearch.trim().toLowerCase();
  const normalizedResultsSearch = resultsSearch.trim().toLowerCase();

  const filteredCourses = useMemo(() => {
    if (!normalizedContentSearch) {
      return courses;
    }

    return courses.filter((course) =>
      [course.title, course.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedContentSearch))
    );
  }, [courses, normalizedContentSearch]);

  const filteredAssessments = useMemo(() => {
    if (!normalizedContentSearch) {
      return assessments;
    }

    return assessments.filter((assessment) =>
      [assessment.title, assessment.type, assessment.access_code, assessment.shared_code]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedContentSearch))
    );
  }, [assessments, normalizedContentSearch]);

  const filteredQuestions = useMemo(() => {
    if (!normalizedContentSearch) {
      return questions;
    }

    return questions.filter((question) => {
      const optionText = (question.options || []).map((option) => option.option_text).join(" ");
      return [question.question_text, question.question_type, optionText]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedContentSearch));
    });
  }, [questions, normalizedContentSearch]);

  const filteredQuestionBank = useMemo(() => {
    const filtered = questionBank.filter((question) => {
      const matchesType = bankTypeFilter === "all" || question.question_type === bankTypeFilter;
      if (!matchesType) {
        return false;
      }

      const matchesRequired =
        bankRequiredFilter === "all" ||
        (bankRequiredFilter === "required" && Boolean(question.is_required)) ||
        (bankRequiredFilter === "optional" && !Boolean(question.is_required));
      if (!matchesRequired) {
        return false;
      }

      if (!normalizedBankSearch) {
        return true;
      }

      const optionText = (question.options || []).map((option) => option.option_text).join(" ");
      return [question.question_text, question.question_type, optionText]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedBankSearch));
    });
    if (bankSort === "points") {
      return filtered.sort((left, right) => {
        const pointDelta = Number(right.points || 0) - Number(left.points || 0);
        return pointDelta !== 0 ? pointDelta : Number(right.id || 0) - Number(left.id || 0);
      });
    }

    return filtered.sort((left, right) => Number(right.id || 0) - Number(left.id || 0));
  }, [questionBank, bankTypeFilter, bankRequiredFilter, bankSort, normalizedBankSearch]);

  const filteredSubmissions = useMemo(() => {
    const filtered = submissions.filter((submission) =>
      [submission.full_name, submission.email, submission.score, submission.answers_count]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedResultsSearch))
    );
    if (submissionSort === "highest") {
      return filtered.sort((left, right) => Number(right.score || 0) - Number(left.score || 0));
    }
    if (submissionSort === "answers") {
      return filtered.sort((left, right) => Number(right.answers_count || 0) - Number(left.answers_count || 0));
    }

    return filtered.sort(
      (left, right) => new Date(right.submitted_at || 0) - new Date(left.submitted_at || 0)
    );
  }, [normalizedResultsSearch, submissions, submissionSort]);

  const filteredCourseStudents = useMemo(() => {
    const students = courseResults?.students || [];
    const filtered = students.filter((student) =>
      [student.full_name, student.email, student.pre_score, student.post_score, student.improvement]
        .filter((value) => value !== null && value !== undefined && value !== "")
        .some((value) => String(value).toLowerCase().includes(normalizedResultsSearch))
    );
    if (courseResultsSort === "improvement") {
      return filtered.sort((left, right) => Number(right.improvement || 0) - Number(left.improvement || 0));
    }
    if (courseResultsSort === "post") {
      return filtered.sort((left, right) => Number(right.post_score || 0) - Number(left.post_score || 0));
    }

    return filtered.sort((left, right) =>
      String(left.full_name || "").localeCompare(String(right.full_name || ""))
    );
  }, [courseResults?.students, normalizedResultsSearch, courseResultsSort]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    fetchCourses();
    fetchQuestionBank();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCourseId) {
      fetchCourseAiReport(selectedCourseId);
    }
    if (selectedAssessmentId) {
      fetchAssessmentAiReport(selectedAssessmentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, selectedCourseId, selectedAssessmentId]);

  useEffect(() => {
    if (!feedback) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setFeedback("");
    }, 3200);

    return () => window.clearTimeout(timeoutId);
  }, [feedback]);

  if (!user?.id) {
    return (
      <section className="panel">
        <div className="empty-panel">
          <p>{loginMessage}</p>
        </div>
      </section>
    );
  }

  const copyText = async (value, successMessage) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setFeedbackType("success");
      setFeedback(successMessage);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(t.copyFailed);
    }
  };

  const fetchCourses = async () => {
    if (!user?.id) {
      setCourses([]);
      setAssessments([]);
      setQuestions([]);
      setSubmissions([]);
      setCourseResults(null);
      setCourseAiReport(null);
      setAssessmentAiReport(null);
      setFeedbackType("error");
      setFeedback(loginMessage);
      return;
    }

    try {
      setIsLoadingCourses(true);
      const response = await api.get(`/trainer/${user.id}/courses`);
      const nextCourses = response.data.courses || [];
      setCourses(nextCourses);

      if (nextCourses.length) {
        const nextCourseId = selectedCourseId || nextCourses[0].id;
        await fetchAssessments(nextCourseId);
        await fetchCourseResults(nextCourseId);
        await fetchCourseAiReport(nextCourseId);
      } else {
        setSelectedCourseId(null);
        setAssessments([]);
        setQuestions([]);
        setSubmissions([]);
        setCourseResults(null);
        setCourseAiReport(null);
      }
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || t.loadCoursesError);
    } finally {
      setIsLoadingCourses(false);
    }
  };

  const fetchAssessments = async (courseId) => {
    if (!courseId) {
      setAssessments([]);
      setSelectedAssessmentId(null);
      setQuestions([]);
      setSubmissions([]);
      setAssessmentAnalytics(null);
      setAssessmentAiReport(null);
      return;
    }

    try {
      setIsLoadingAssessments(true);
      const response = await api.get(`/courses/${courseId}/assessments`, {
        params: { trainer_id: user.id }
      });
      const nextAssessments = response.data.assessments || [];
      setAssessments(nextAssessments);
      setSelectedCourseId(courseId);

      if (nextAssessments.length) {
        const nextAssessmentId =
          nextAssessments.find((assessment) => assessment.id === selectedAssessmentId)?.id ||
          nextAssessments[0].id;
        await fetchAssessmentWorkspace(nextAssessmentId);
      } else {
        setSelectedAssessmentId(null);
        setQuestions([]);
        setSubmissions([]);
        setAssessmentAnalytics(null);
        setAssessmentAiReport(null);
      }
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || t.loadAssessmentsError);
    } finally {
      setIsLoadingAssessments(false);
    }
  };

  const fetchAssessmentWorkspace = async (assessmentId) => {
    setSelectedAssessmentId(assessmentId);
    await Promise.all([
      fetchQuestions(assessmentId),
      fetchSubmissions(assessmentId),
      fetchAssessmentAnalytics(assessmentId),
      fetchAssessmentAiReport(assessmentId)
    ]);
  };

  const fetchQuestions = async (assessmentId) => {
    if (!assessmentId) {
      setQuestions([]);
      return;
    }

    try {
      setIsLoadingQuestions(true);
      const response = await api.get(`/assessments/${assessmentId}/questions`, {
        params: { trainer_id: user.id }
      });
      setQuestions(response.data.questions || []);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || t.loadQuestionsError);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const fetchQuestionBank = async () => {
    try {
      setIsLoadingQuestionBank(true);
      const response = await api.get(`/trainer/${user.id}/question-bank`);
      setQuestionBank(response.data.questions || []);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || bankText.loadError);
    } finally {
      setIsLoadingQuestionBank(false);
    }
  };

  const fetchSubmissions = async (assessmentId) => {
    if (!assessmentId) {
      setSubmissions([]);
      setSubmissionDetails(null);
      return;
    }

    try {
      setIsLoadingResults(true);
      const response = await api.get(`/assessments/${assessmentId}/submissions`, {
        params: { trainer_id: user.id }
      });
      const nextSubmissions = response.data.submissions || [];
      setSubmissions(nextSubmissions);

      if (nextSubmissions.length) {
        const nextSubmissionId =
          nextSubmissions.find((submission) => submission.id === selectedSubmissionId)?.id ||
          nextSubmissions[0].id;
        await fetchSubmissionDetails(nextSubmissionId);
      } else {
        setSelectedSubmissionId(null);
        setSubmissionDetails(null);
      }
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || t.loadSubmissionsError);
    } finally {
      setIsLoadingResults(false);
    }
  };

  const fetchSubmissionDetails = async (submissionId) => {
    if (!submissionId) {
      setSubmissionDetails(null);
      return;
    }

    try {
      const response = await api.get(`/submissions/${submissionId}`, {
        params: { trainer_id: user.id }
      });
      setSelectedSubmissionId(submissionId);
      setSubmissionDetails(response.data);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || t.loadStudentAnswersError);
    }
  };

  const fetchAssessmentAnalytics = async (assessmentId) => {
    if (!assessmentId) {
      setAssessmentAnalytics(null);
      return;
    }

    try {
      const response = await api.get(`/get_analytics/${assessmentId}`);
      setAssessmentAnalytics(response.data);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || t.loadAnalyticsError);
    }
  };

  const fetchCourseResults = async (courseId) => {
    if (!courseId) {
      setCourseResults(null);
      return;
    }

    try {
      const response = await api.get(`/courses/${courseId}/results-summary`, {
        params: { trainer_id: user.id }
      });
      setCourseResults(response.data);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || t.loadCourseResultsError);
    }
  };

  const fetchCourseAiReport = async (courseId) => {
    if (!courseId) {
      setCourseAiReport(null);
      return;
    }

    try {
      const response = await api.get(`/courses/${courseId}/ai-report`, {
        params: { trainer_id: user.id, lang: language }
      });
      setCourseAiReport(response.data);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || t.loadCourseAiError);
    }
  };

  const fetchAssessmentAiReport = async (assessmentId) => {
    if (!assessmentId) {
      setAssessmentAiReport(null);
      return;
    }

    try {
      const response = await api.get(`/assessments/${assessmentId}/ai-report`, {
        params: { trainer_id: user.id, lang: language }
      });
      setAssessmentAiReport(response.data);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || t.loadAssessmentAiError);
    }
  };

  const resetCourseForm = () => {
    setCourseForm(emptyCourseForm);
    setEditingCourseId(null);
  };

  const resetAssessmentForm = () => {
    setAssessmentForm(emptyAssessmentForm);
    setEditingAssessmentId(null);
  };

  const resetQuestionForm = () => {
    setQuestionForm(emptyQuestionForm);
    setEditingQuestionId(null);
    setEditingBankQuestionId(null);
  };

  const buildOptionsPayload = () => {
    if (questionForm.question_type === "essay") return [];

    if (questionForm.question_type === "true_false") {
      return [
        { option_text: isArabic ? "صح" : "True", is_correct: true },
        { option_text: isArabic ? "خطأ" : "False", is_correct: false }
      ];
    }

    if (questionForm.question_type === "rating") {
      return Array.from({ length: 11 }, (_, index) => ({
        option_text: index.toString(),
        is_correct: false
      }));
    }

    return questionForm.options
      .map((option, index) => ({ option_text: option.trim(), is_correct: index === 0 }))
      .filter((option) => option.option_text);
  };

  const submitCourse = async () => {
    if (!courseForm.title.trim()) {
      setFeedbackType("error");
      setFeedback(isArabic ? "عنوان الكورس مطلوب" : "Course title is required");
      return;
    }

    try {
      if (editingCourseId) {
        await api.put(`/courses/${editingCourseId}`, {
          trainer_id: user.id,
          title: courseForm.title,
          description: courseForm.description
        });
        setFeedbackType("success");
        setFeedback(isArabic ? "تم تحديث الكورس بنجاح" : "Course updated successfully");
      } else {
        await api.post("/create_course", {
          trainer_id: user.id,
          title: courseForm.title,
          description: courseForm.description
        });
        setFeedbackType("success");
        setFeedback(isArabic ? "تم إنشاء الكورس بنجاح" : "Course created successfully");
      }

      resetCourseForm();
      await fetchCourses();
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || (isArabic ? "تعذر حفظ الكورس" : "Unable to save course"));
    }
  };

  const submitAssessment = async () => {
    if (!selectedCourseId) {
      setFeedbackType("error");
      setFeedback(isArabic ? "اختر كورسًا أولًا" : "Choose a course first");
      return;
    }

    if (!assessmentForm.title.trim()) {
      setFeedbackType("error");
      setFeedback(isArabic ? "عنوان التقييم مطلوب" : "Assessment title is required");
      return;
    }

    try {
      if (editingAssessmentId) {
        await api.put(`/assessments/${editingAssessmentId}`, {
          trainer_id: user.id,
          title: assessmentForm.title,
          is_timed: false,
          duration_minutes: null,
          show_results_to_student: assessmentForm.show_results_to_student,
          show_answers_to_student: assessmentForm.show_answers_to_student,
          show_ai_report_to_student: assessmentForm.show_ai_report_to_student
        });
        setFeedbackType("success");
        setFeedback(isArabic ? "تم تحديث إعدادات التقييم بنجاح" : "Assessment settings updated successfully");
      } else {
        await api.post("/create_assessment", {
          course_id: selectedCourseId,
          title: assessmentForm.title,
          show_results_to_student: assessmentForm.show_results_to_student,
          show_answers_to_student: assessmentForm.show_answers_to_student,
          show_ai_report_to_student: assessmentForm.show_ai_report_to_student
        });
        setFeedbackType("success");
        setFeedback(isArabic ? "تم إنشاء التقييمين القبلي والبعدي بنجاح" : "Pre and post assessments were created successfully");
      }

      resetAssessmentForm();
      await fetchAssessments(selectedCourseId);
      await fetchCourseResults(selectedCourseId);
      await fetchCourseAiReport(selectedCourseId);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || (isArabic ? "تعذر حفظ التقييم" : "Unable to save assessment"));
    }
  };

  const submitQuestion = async () => {
    if (!selectedAssessmentId) {
      setFeedbackType("error");
      setFeedback(isArabic ? "اختر تقييمًا أولًا" : "Choose an assessment first");
      return;
    }

    if (!questionForm.question_text.trim()) {
      setFeedbackType("error");
      setFeedback(isArabic ? "نص السؤال مطلوب" : "Question text is required");
      return;
    }

    const options = buildOptionsPayload();
    if (questionForm.question_type === "mcq" && options.length < 2) {
      setFeedbackType("error");
      setFeedback(isArabic ? "أضف خيارين على الأقل لسؤال الاختيار من متعدد" : "Add at least two options for a multiple-choice question");
      return;
    }

    const payload = {
      trainer_id: user.id,
      assessment_id: selectedAssessmentId,
      question_text: questionForm.question_text,
      question_type: questionForm.question_type,
      points: Number(questionForm.points) || 1,
      question_order: Number(questionForm.question_order) || 1,
      options
    };

    try {
      if (editingQuestionId) {
        await api.put(`/questions/${editingQuestionId}`, payload);
        setFeedbackType("success");
        setFeedback(isArabic ? "تم تحديث السؤال بنجاح" : "Question updated successfully");
      } else {
        await api.post("/add_question", payload);
        setFeedbackType("success");
        setFeedback(isArabic ? "تم إنشاء السؤال بنجاح" : "Question created successfully");
      }

      resetQuestionForm();
      await fetchQuestions(selectedAssessmentId);
      await fetchAssessments(selectedCourseId);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || (isArabic ? "تعذر حفظ السؤال" : "Unable to save question"));
    }
  };

  const saveCurrentQuestionToBank = async () => {
    if (!questionForm.question_text.trim()) {
      setFeedbackType("error");
      setFeedback(isArabic ? "نص السؤال مطلوب" : "Question text is required");
      return;
    }

    const options = buildOptionsPayload();
    if (questionForm.question_type === "mcq" && options.length < 2) {
      setFeedbackType("error");
      setFeedback(isArabic ? "أضف خيارين على الأقل لسؤال الاختيار من متعدد" : "Add at least two options for a multiple-choice question");
      return;
    }

    try {
      const payload = {
        trainer_id: user.id,
        question_text: questionForm.question_text,
        question_type: questionForm.question_type,
        points: Number(questionForm.points) || 1,
        is_required: true,
        options
      };
      if (editingBankQuestionId) {
        await api.put(`/question-bank/${editingBankQuestionId}`, payload);
      } else {
        await api.post("/question-bank", payload);
      }
      setFeedbackType("success");
      setFeedback(editingBankQuestionId ? bankText.updated : bankText.saved);
      resetQuestionForm();
      await fetchQuestionBank();
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || (editingBankQuestionId ? bankText.updateError : bankText.saveError));
    }
  };

  const startEditCourse = (course) => {
    setCourseForm({ title: course.title || "", description: course.description || "" });
    setEditingCourseId(course.id);
  };

  const startEditAssessment = (assessment) => {
    setAssessmentForm({
      title: assessment.title || "",
      type: assessment.type || "pre",
      show_results_to_student: Boolean(assessment.show_results_to_student),
      show_answers_to_student: Boolean(assessment.show_answers_to_student),
      show_ai_report_to_student: Boolean(assessment.show_ai_report_to_student)
    });
    setEditingAssessmentId(assessment.id);
  };

  const startEditQuestion = (question) => {
    const mcqOptions =
      question.question_type === "mcq"
        ? (question.options || []).map((option) => option.option_text)
        : ["", ""];
    setQuestionForm({
      question_text: question.question_text || "",
      question_type: question.question_type || "essay",
      points: question.points || 1,
      question_order: question.question_order || 1,
      options: mcqOptions.length ? mcqOptions : ["", ""]
    });
    setEditingQuestionId(question.id);
    setEditingBankQuestionId(null);
    setActiveTab("management");
  };

  const startEditBankQuestion = (bankQuestion) => {
    const bankOptions =
      bankQuestion.question_type === "mcq"
        ? (bankQuestion.options || []).map((option) => option.option_text)
        : ["", ""];
    setQuestionForm({
      question_text: bankQuestion.question_text || "",
      question_type: bankQuestion.question_type || "essay",
      points: bankQuestion.points || 1,
      question_order: 1,
      options: bankOptions.length ? bankOptions : ["", ""]
    });
    setEditingQuestionId(null);
    setEditingBankQuestionId(bankQuestion.id);
    setActiveTab("management");
    scrollToSection(questionFormRef);
  };

  const saveExistingQuestionToBank = async (question) => {
    const options =
      question.question_type === "essay"
        ? []
        : (question.options || []).map((option, index) => ({
            option_text: option.option_text,
            is_correct: Boolean(option.is_correct ?? index === 0),
            option_order: option.option_order || index + 1
          }));

    try {
      await api.post("/question-bank", {
        trainer_id: user.id,
        question_text: question.question_text,
        question_type: question.question_type,
        points: Number(question.points) || 1,
        is_required: true,
        options
      });
      setFeedbackType("success");
      setFeedback(bankText.saved);
      await fetchQuestionBank();
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || bankText.saveError);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    setConfirmState({
      title: isArabic ? "حذف الكورس" : "Delete Course",
      message: isArabic ? "هل أنت متأكد أنك تريد حذف هذا الكورس؟" : "Are you sure you want to delete this course?",
      action: async () => {
        try {
          await api.delete(`/courses/${courseId}`, { params: { trainer_id: user.id } });
          setFeedbackType("success");
          setFeedback(isArabic ? "تم حذف الكورس بنجاح" : "Course deleted successfully");
          await fetchCourses();
        } catch (error) {
          setFeedbackType("error");
          setFeedback(error.response?.data?.error || (isArabic ? "تعذر حذف الكورس" : "Unable to delete course"));
        } finally {
          setConfirmState(null);
        }
      }
    });
  };

  const handleDeleteAssessment = async (assessmentId) => {
    setConfirmState({
      title: isArabic ? "حذف التقييم" : "Delete Assessment",
      message: isArabic ? "هل أنت متأكد أنك تريد حذف هذا التقييم؟" : "Are you sure you want to delete this assessment?",
      action: async () => {
        try {
          await api.delete(`/assessments/${assessmentId}`, { params: { trainer_id: user.id } });
          setFeedbackType("success");
          setFeedback(isArabic ? "تم حذف التقييم بنجاح" : "Assessment deleted successfully");
          await fetchAssessments(selectedCourseId);
          await fetchCourseResults(selectedCourseId);
          await fetchCourseAiReport(selectedCourseId);
        } catch (error) {
          setFeedbackType("error");
          setFeedback(error.response?.data?.error || (isArabic ? "تعذر حذف التقييم" : "Unable to delete assessment"));
        } finally {
          setConfirmState(null);
        }
      }
    });
  };

  const handleDeleteQuestion = async (questionId) => {
    setConfirmState({
      title: isArabic ? "حذف السؤال" : "Delete Question",
      message: isArabic ? "هل أنت متأكد أنك تريد حذف هذا السؤال؟" : "Are you sure you want to delete this question?",
      action: async () => {
        try {
          await api.delete(`/questions/${questionId}`, { params: { trainer_id: user.id } });
          setFeedbackType("success");
          setFeedback(isArabic ? "تم حذف السؤال بنجاح" : "Question deleted successfully");
          await fetchQuestions(selectedAssessmentId);
          await fetchAssessments(selectedCourseId);
        } catch (error) {
          setFeedbackType("error");
          setFeedback(error.response?.data?.error || (isArabic ? "تعذر حذف السؤال" : "Unable to delete question"));
        } finally {
          setConfirmState(null);
        }
      }
    });
  };

  const handleDeleteBankQuestion = async (bankQuestionId) => {
    setConfirmState({
      title: bankText.deleteTitle,
      message: bankText.deleteMessage,
      action: async () => {
        try {
          await api.delete(`/question-bank/${bankQuestionId}`, { params: { trainer_id: user.id } });
          setFeedbackType("success");
          setFeedback(bankText.deleted);
          await fetchQuestionBank();
        } catch (error) {
          setFeedbackType("error");
          setFeedback(error.response?.data?.error || bankText.deleteError);
        } finally {
          setConfirmState(null);
        }
      }
    });
  };

  const addBankQuestionToAssessment = async (bankQuestionId) => {
    if (!selectedAssessmentId) {
      setFeedbackType("error");
      setFeedback(bankText.chooseAssessment);
      return;
    }

    try {
      await api.post(`/question-bank/${bankQuestionId}/add-to-assessment`, {
        trainer_id: user.id,
        assessment_id: selectedAssessmentId
      });
      setFeedbackType("success");
      setFeedback(bankText.added);
      await fetchQuestions(selectedAssessmentId);
      await fetchAssessments(selectedCourseId);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || bankText.addError);
    }
  };

  const addFilteredBankQuestionsToAssessment = async () => {
    if (!selectedAssessmentId) {
      setFeedbackType("error");
      setFeedback(bankText.chooseAssessment);
      return;
    }

    if (!filteredQuestionBank.length) {
      setFeedbackType("error");
      setFeedback(noSearchResults);
      return;
    }

    try {
      await api.post("/question-bank/add-many-to-assessment", {
        trainer_id: user.id,
        assessment_id: selectedAssessmentId,
        bank_question_ids: filteredQuestionBank.map((question) => question.id)
      });
      setFeedbackType("success");
      setFeedback(bankText.bulkAdded);
      await fetchQuestions(selectedAssessmentId);
      await fetchAssessments(selectedCourseId);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || bankText.bulkAddError);
    }
  };


  const updateQuestionOption = (index, value) => {
    const nextOptions = [...questionForm.options];
    nextOptions[index] = value;
    setQuestionForm((current) => ({ ...current, options: nextOptions }));
  };

  const scrollToSection = (ref) => {
    ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const exportRowsToCsv = (rows, filename) => {
    if (!rows.length) {
      setFeedbackType("error");
      setFeedback(isArabic ? "لا توجد بيانات للتصدير الآن" : "There is no data to export right now");
      return;
    }

    const escapeValue = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv = rows.map((row) => row.map(escapeValue).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setFeedbackType("success");
    setFeedback(isArabic ? "تم تصدير الملف بنجاح" : "File exported successfully");
  };

  const exportSubmissionsCsv = () => {
    const rows = [
      [t.student, "Email", t.score, t.answersCount]
    ];
    filteredSubmissions.forEach((submission) => {
      rows.push([
        submission.full_name,
        submission.email,
        submission.score,
        submission.answers_count
      ]);
    });
    exportRowsToCsv(rows, `submissions-${selectedAssessment?.title || "assessment"}.csv`);
  };

  const exportCourseResultsCsv = () => {
    const rows = [
      [t.student, t.pre, t.post, t.improvement]
    ];
    filteredCourseStudents.forEach((student) => {
      rows.push([
        student.full_name,
        student.pre_score ?? "",
        student.post_score ?? "",
        student.improvement ?? ""
      ]);
    });
    exportRowsToCsv(rows, `course-results-${courseResults?.course?.title || "course"}.csv`);
  };

  const exportTextFile = (content, filename) => {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setFeedbackType("success");
    setFeedback(isArabic ? "تم تصدير التقرير بنجاح" : "Report exported successfully");
  };

  const openPrintableReport = (title, sections) => {
    const normalizedSections = (sections || []).filter((section) => section && section.content);
    if (!normalizedSections.length) {
      setFeedbackType("error");
      setFeedback(pdfText.pdfEmpty);
      return;
    }

    const printWindow = window.open("", "_blank", "width=960,height=720");
    if (!printWindow) {
      setFeedbackType("error");
      setFeedback(pdfText.pdfEmpty);
      return;
    }

    const safeTitle = String(title || "Report")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    const htmlSections = normalizedSections
      .map((section) => {
        const heading = String(section.heading || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
        const content = String(section.content || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\n/g, "<br />");
        return `<section><h2>${heading}</h2><div>${content}</div></section>`;
      })
      .join("");

    printWindow.document.write(`<!doctype html><html lang="${language}" dir="${isArabic ? "rtl" : "ltr"}"><head><meta charset="utf-8" /><title>${safeTitle}</title><style>body{font-family:Arial,sans-serif;padding:32px;color:#173042;line-height:1.7}h1{margin-bottom:18px}h2{margin:24px 0 10px;color:#21445d}section{margin-bottom:18px;padding-bottom:18px;border-bottom:1px solid #d9e4ec}</style></head><body><h1>${safeTitle}</h1>${htmlSections}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    setFeedbackType("success");
    setFeedback(pdfText.pdfReady);
  };

  const exportCourseAiReportText = () => {
    if (!courseAiReport?.report) {
      setFeedbackType("error");
      setFeedback(isArabic ? "لا يوجد تقرير لتصديره الآن" : "There is no report to export right now");
      return;
    }

    const report = courseAiReport.report;
    const content = [
      report.headline,
      "",
      report.summary,
      "",
      `${t.strengths}:`,
      ...(report.strengths?.length ? report.strengths.map((item) => `- ${item}`) : ["-"]),
      "",
      `${t.risks}:`,
      ...(report.risks?.length ? report.risks.map((item) => `- ${item}`) : ["-"]),
      "",
      `${t.recommendations}:`,
      ...(report.recommendations?.length ? report.recommendations.map((item) => `- ${item}`) : ["-"])
    ].join("\n");

    exportTextFile(content, `ai-course-report-${courseResults?.course?.title || "course"}.txt`);
  };

  const copyCourseAiReport = () => {
    if (!courseAiReport?.report) {
      setFeedbackType("error");
      setFeedback(isArabic ? "لا يوجد تقرير متاح للنسخ الآن" : "There is no report to copy right now");
      return;
    }

    const report = courseAiReport.report;
    const content = [
      report.headline,
      "",
      report.summary,
      "",
      `${t.strengths}:`,
      ...(report.strengths?.length ? report.strengths.map((item) => `- ${item}`) : ["-"]),
      "",
      `${t.risks}:`,
      ...(report.risks?.length ? report.risks.map((item) => `- ${item}`) : ["-"]),
      "",
      `${t.recommendations}:`,
      ...(report.recommendations?.length ? report.recommendations.map((item) => `- ${item}`) : ["-"])
    ].join("\n");

    copyText(content, isArabic ? "تم نسخ تقرير الكورس" : "Course report copied successfully");
  };

  const exportCourseAiReportPdf = () => {
    if (!courseAiReport?.report) {
      setFeedbackType("error");
      setFeedback(pdfText.pdfEmpty);
      return;
    }

    const report = courseAiReport.report;
    openPrintableReport(report.headline || t.aiCourseReport, [
      { heading: t.courseSummary, content: report.summary || "" },
      { heading: t.strengths, content: (report.strengths || []).join("\n") || "-" },
      { heading: t.risks, content: (report.risks || []).join("\n") || "-" },
      { heading: t.recommendations, content: (report.recommendations || []).join("\n") || "-" }
    ]);
  };

  const exportAssessmentAiReportText = () => {
    if (!assessmentAiReport?.report) {
      setFeedbackType("error");
      setFeedback(isArabic ? "لا يوجد تقرير لتصديره الآن" : "There is no report to export right now");
      return;
    }

    const report = assessmentAiReport.report;
    const content = [
      report.headline,
      "",
      report.summary,
      "",
      `${t.dominantSentiment}: ${report.dominant_sentiment}`,
      `${t.dominantLevel}: ${report.dominant_level}`,
      `${t.topKeywords}: ${(report.top_keywords || []).join(", ")}`,
      "",
      `${t.recommendations}:`,
      ...(report.recommendations?.length ? report.recommendations.map((item) => `- ${item}`) : ["-"])
    ].join("\n");

    exportTextFile(content, `ai-assessment-report-${selectedAssessment?.title || "assessment"}.txt`);
  };

  const copyAssessmentAiReport = () => {
    if (!assessmentAiReport?.report) {
      setFeedbackType("error");
      setFeedback(isArabic ? "لا يوجد تقرير متاح للنسخ الآن" : "There is no report to copy right now");
      return;
    }

    const report = assessmentAiReport.report;
    const content = [
      report.headline,
      "",
      report.summary,
      "",
      `${t.dominantSentiment}: ${report.dominant_sentiment}`,
      `${t.dominantLevel}: ${report.dominant_level}`,
      `${t.topKeywords}: ${(report.top_keywords || []).join(", ")}`,
      "",
      `${t.recommendations}:`,
      ...(report.recommendations?.length ? report.recommendations.map((item) => `- ${item}`) : ["-"])
    ].join("\n");

    copyText(content, isArabic ? "تم نسخ تقرير التقييم" : "Assessment report copied successfully");
  };

  const exportAssessmentAiReportPdf = () => {
    if (!assessmentAiReport?.report) {
      setFeedbackType("error");
      setFeedback(pdfText.pdfEmpty);
      return;
    }

    const report = assessmentAiReport.report;
    openPrintableReport(report.headline || t.aiAssessmentReport, [
      { heading: t.assessmentSummary, content: report.summary || "" },
      { heading: t.dominantSentiment, content: report.dominant_sentiment || "-" },
      { heading: t.dominantLevel, content: report.dominant_level || "-" },
      { heading: t.topKeywords, content: (report.top_keywords || []).join(", ") || "-" },
      { heading: t.recommendations, content: (report.recommendations || []).join("\n") || "-" }
    ]);
  };

  const sentimentChartData = assessmentAnalytics
    ? [
        { name: t.positive, value: assessmentAnalytics.analytics.sentiment_distribution.positive },
        { name: t.neutral, value: assessmentAnalytics.analytics.sentiment_distribution.neutral },
        { name: t.negative, value: assessmentAnalytics.analytics.sentiment_distribution.negative }
      ]
    : [];

  const levelChartData = assessmentAnalytics
    ? [
        { name: t.high, value: assessmentAnalytics.analytics.levels_distribution.High },
        { name: t.medium, value: assessmentAnalytics.analytics.levels_distribution.Medium },
        { name: t.low, value: assessmentAnalytics.analytics.levels_distribution.Low }
      ]
    : [];

  const improvementChartData =
    courseResults?.students
      ?.filter((student) => student.improvement !== null)
      .map((student) => ({ name: student.full_name, improvement: student.improvement })) || [];

  const renderHierarchyLists = () => (
    <div className="stack-lg">
      <div className="dashboard-card">
        <div className="card-title-row">
          <h3>{t.courses}</h3>
          <span className="status-pill">{filteredCourses.length}</span>
        </div>
        <div className="field-stack">
          <label className="field-label">{contentSearchLabel}</label>
          <input
            className="text-input"
            placeholder={contentSearchPlaceholder}
            value={contentSearch}
            onChange={(event) => setContentSearch(event.target.value)}
          />
        </div>
        {isLoadingCourses && <p className="helper-text">{t.loadingCourses}</p>}
        {!isLoadingCourses && !courses.length && (
          <div className="empty-panel compact-panel"><p>{t.noCourses}</p></div>
        )}
        {!isLoadingCourses && !!courses.length && !filteredCourses.length && (
          <div className="empty-panel compact-panel"><p>{noSearchResults}</p></div>
        )}
        <div className="entity-list">
          {filteredCourses.map((course) => (
            <div key={course.id} className={selectedCourseId === course.id ? "entity-card entity-card--active" : "entity-card"}>
              <button className="entity-main" onClick={async () => { await fetchAssessments(course.id); await fetchCourseResults(course.id); await fetchCourseAiReport(course.id); }}>
                <strong>{course.title}</strong>
                <span>{course.assessments_count} {t.assessmentsCount} - {course.submissions_count} {t.submissionsCount}</span>
              </button>
              <div className="entity-actions">
                <button className="ghost-button" onClick={() => startEditCourse(course)}>{t.edit}</button>
                <button className="danger-button" onClick={() => handleDeleteCourse(course.id)}>{t.delete}</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="dashboard-card">
        <div className="card-title-row">
          <h3>{t.assessments}</h3>
          {selectedCourse && <span className="helper-text">{selectedCourse.title}</span>}
        </div>
        {isLoadingAssessments && <p className="helper-text">{t.loadingAssessments}</p>}
        {!isLoadingAssessments && selectedCourse && !assessments.length && <div className="empty-panel compact-panel"><p>{t.noAssessments}</p></div>}
        {!isLoadingAssessments && !!assessments.length && !filteredAssessments.length && <div className="empty-panel compact-panel"><p>{noSearchResults}</p></div>}
        {!selectedCourse && <div className="empty-panel compact-panel"><p>{t.chooseCourseToSeeAssessments}</p></div>}
        <div className="entity-list">
          {filteredAssessments.map((assessment) => (
            <div key={assessment.id} className={selectedAssessmentId === assessment.id ? "entity-card entity-card--active" : "entity-card"}>
              <button className="entity-main" onClick={() => fetchAssessmentWorkspace(assessment.id)}>
                <strong>{assessment.title}</strong>
                <span>{assessment.type === "pre" ? t.pre : t.post} - {assessment.questions_count} {t.questions} - {t.codeLabel} {assessment.access_code}</span>
              </button>
              <div className="entity-actions">
                <button className="ghost-button" onClick={() => startEditAssessment(assessment)}>{t.edit}</button>
                <button className="ghost-button" onClick={() => copyText(assessment.access_code, t.copiedCode)}>{t.copyCode}</button>
                <button className="danger-button" onClick={() => handleDeleteAssessment(assessment.id)}>{t.delete}</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="dashboard-card">
        <div className="card-title-row">
          <h3>{t.questions}</h3>
          {selectedAssessment && <span className="helper-text">{selectedAssessment.title}</span>}
        </div>
        {isLoadingQuestions && <p className="helper-text">{t.loadingQuestions}</p>}
        {!isLoadingQuestions && selectedAssessment && !questions.length && <div className="empty-panel compact-panel"><p>{t.noQuestions}</p></div>}
        {!isLoadingQuestions && !!questions.length && !filteredQuestions.length && <div className="empty-panel compact-panel"><p>{noSearchResults}</p></div>}
        {!selectedAssessment && <div className="empty-panel compact-panel"><p>{t.chooseAssessmentToSeeQuestions}</p></div>}
        <div className="entity-list">
          {filteredQuestions.map((question) => (
            <div key={question.id} className="entity-card entity-card--stack">
              <div className="entity-main entity-main--static">
                <strong>{question.question_text}</strong>
                <span>{questionTypeLabel(question.question_type)} - {question.points} {t.points} - {t.order} {question.question_order}</span>
                {!!question.options?.length && <span className="helper-text">{t.options}: {question.options.map((option) => option.option_text).join(" / ")}</span>}
              </div>
              <div className="entity-actions">
                <button className="ghost-button" onClick={() => startEditQuestion(question)}>{t.edit}</button>
                <button className="ghost-button" onClick={() => saveExistingQuestionToBank(question)}>{bankText.saveExisting}</button>
                <button className="danger-button" onClick={() => handleDeleteQuestion(question.id)}>{t.delete}</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderManagementView = () => (
    <section className="stack-lg">
      <div className="panel">
        <div className="section-header">
          <div>
            <p className="eyebrow">{t.trainerWorkspace}</p>
            <h2 className="section-title">{t.contentManagement}</h2>
          </div>
        </div>
        <div className="stack-lg">
          <div className="dashboard-card">
            <div className="card-title-row">
              <div>
                <h3>{t.summaryTitle}</h3>
                <p className="helper-text">{t.summaryHint}</p>
              </div>
            </div>
            <div className="details-grid">
              <div className="detail-chip">
                <span>{t.courses}</span>
                <strong>{courses.length}</strong>
              </div>
              <div className="detail-chip">
                <span>{t.assessments}</span>
                <strong>{assessments.length}</strong>
              </div>
              <div className="detail-chip">
                <span>{t.questions}</span>
                <strong>{questions.length}</strong>
              </div>
              <div className="detail-chip">
                <span>{bankText.title}</span>
                <strong>{questionBank.length}</strong>
              </div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-title-row">
              <div>
                <h3>{t.builderTitle}</h3>
                <p className="helper-text">{t.builderHint}</p>
              </div>
            </div>
            <div className="management-split">
              <div className="stack-lg">
                <div className="dashboard-card dashboard-card--nested" ref={courseFormRef}>
                  <div className="card-title-row">
                    <h3>{editingCourseId ? t.editCourse : t.createCourse}</h3>
                    {editingCourseId && <button className="ghost-button" onClick={resetCourseForm}>{t.cancelEdit}</button>}
                  </div>
                  <div className="field-stack">
                    <label className="field-label">{t.courseTitle}</label>
                    <input placeholder={t.courseTitle} value={courseForm.title} onChange={(e) => setCourseForm((current) => ({ ...current, title: e.target.value }))} className="text-input" />
                  </div>
                  <div className="field-stack">
                    <label className="field-label">{t.courseDescription}</label>
                    <textarea placeholder={t.courseDescription} value={courseForm.description} onChange={(e) => setCourseForm((current) => ({ ...current, description: e.target.value }))} className="text-area" />
                  </div>
                  <button className="primary-button" onClick={submitCourse}>{editingCourseId ? t.saveCourseButton : t.createCourseButton}</button>
                </div>

                <div className="dashboard-card dashboard-card--nested" ref={assessmentFormRef}>
                  <div className="card-title-row">
                    <h3>{editingAssessmentId ? t.editAssessment : t.createAssessment}</h3>
                    {editingAssessmentId && <button className="ghost-button" onClick={resetAssessmentForm}>{t.cancelEdit}</button>}
                  </div>
                  <p className="helper-text">{selectedCourse ? `${t.currentCourse}: ${selectedCourse.title}` : t.selectCourseFirst}</p>
                  <div className="field-stack">
                    <label className="field-label">{t.assessmentTitle}</label>
                    <input placeholder={t.assessmentTitle} value={assessmentForm.title} onChange={(e) => setAssessmentForm((current) => ({ ...current, title: e.target.value }))} className="text-input" />
                  </div>
                  <p className="helper-text">{t.assessmentHint}</p>
                  <label className="option-row">
                    <input type="checkbox" checked={assessmentForm.show_results_to_student} onChange={(e) => setAssessmentForm((current) => ({ ...current, show_results_to_student: e.target.checked }))} />
                    {t.allowStudentSeeScore}
                  </label>
                  <label className="option-row">
                    <input type="checkbox" checked={assessmentForm.show_answers_to_student} onChange={(e) => setAssessmentForm((current) => ({ ...current, show_answers_to_student: e.target.checked }))} />
                    {t.allowStudentSeeAnswers}
                  </label>
                  <label className="option-row">
                    <input type="checkbox" checked={assessmentForm.show_ai_report_to_student} onChange={(e) => setAssessmentForm((current) => ({ ...current, show_ai_report_to_student: e.target.checked }))} />
                    {t.allowStudentSeeAi}
                  </label>
                  <button className="primary-button" onClick={submitAssessment}>{editingAssessmentId ? t.saveAssessmentButton : t.createAssessmentButton}</button>
                </div>
              </div>

              <div className="stack-lg">
                <div className="dashboard-card dashboard-card--nested" ref={questionFormRef}>
                  <div className="card-title-row">
                    <h3>{editingQuestionId ? t.editQuestion : editingBankQuestionId ? bankText.edit : t.createQuestion}</h3>
                    {(editingQuestionId || editingBankQuestionId) && <button className="ghost-button" onClick={resetQuestionForm}>{t.cancelEdit}</button>}
                  </div>
                  <p className="helper-text">
                    {editingBankQuestionId
                      ? bankText.editHint
                      : selectedAssessment
                        ? `${t.currentAssessment}: ${selectedAssessment.title}`
                        : t.selectAssessmentFirst}
                  </p>
                  <div className="field-stack">
                    <label className="field-label">{t.questionText}</label>
                    <textarea placeholder={t.questionTextPlaceholder} value={questionForm.question_text} onChange={(e) => setQuestionForm((current) => ({ ...current, question_text: e.target.value }))} className="text-area" />
                  </div>
                  <div className="field-stack">
                    <label className="field-label">{t.questionType}</label>
                    <select value={questionForm.question_type} onChange={(e) => setQuestionForm((current) => ({ ...current, question_type: e.target.value, options: e.target.value === "mcq" ? current.options : ["", ""] }))} className="text-input">
                      <option value="essay">{t.essay}</option>
                      <option value="mcq">{t.mcq}</option>
                      <option value="true_false">{t.trueFalse}</option>
                      <option value="rating">{t.rating}</option>
                    </select>
                  </div>
                  <div className="field-grid">
                    <div className="field-stack">
                      <label className="field-label">{t.questionPoints}</label>
                      <input type="number" min="1" placeholder={t.questionPointsPlaceholder} value={questionForm.points} onChange={(e) => setQuestionForm((current) => ({ ...current, points: e.target.value }))} className="text-input" />
                      <p className="field-hint">{t.questionPointsHint}</p>
                    </div>
                    <div className="field-stack">
                      <label className="field-label">{t.questionOrder}</label>
                      <input type="number" min="1" placeholder={t.questionOrderPlaceholder} value={questionForm.question_order} onChange={(e) => setQuestionForm((current) => ({ ...current, question_order: e.target.value }))} className="text-input" />
                      <p className="field-hint">{t.questionOrderHint}</p>
                    </div>
                  </div>
                  {questionForm.question_type === "mcq" && questionForm.options.map((option, index) => (
                    <input key={index} placeholder={`${t.optionLabel} ${index + 1}${index === 0 ? ` (${t.correctAnswer})` : ""}`} value={option} onChange={(e) => updateQuestionOption(index, e.target.value)} className="text-input" />
                  ))}
                  {questionForm.question_type === "mcq" && <button className="ghost-button" onClick={() => setQuestionForm((current) => ({ ...current, options: [...current.options, ""] }))}>{t.addOption}</button>}
                  <p className="helper-text">{bankText.saveHint}</p>
                  <div className="inline-form">
                    <button className="primary-button" onClick={submitQuestion}>{editingQuestionId ? t.saveQuestionButton : t.createQuestionButton}</button>
                    <button className="secondary-button" onClick={saveCurrentQuestionToBank}>{editingBankQuestionId ? bankText.update : bankText.save}</button>
                  </div>
                </div>

                <div className="dashboard-card dashboard-card--nested" ref={questionBankRef}>
                  <div className="card-title-row">
                    <h3>{bankText.title}</h3>
                    <div className="entity-actions">
                      <span className="helper-text">{filteredQuestionBank.length}</span>
                      <button className="ghost-button" disabled={!selectedAssessmentId || !filteredQuestionBank.length} onClick={addFilteredBankQuestionsToAssessment}>
                        {bankText.addAll}
                      </button>
                    </div>
                  </div>
                  <p className="helper-text">{bankText.hint}</p>
                  <div className="field-grid">
                    <div className="field-stack">
                      <label className="field-label">{contentSearchLabel}</label>
                      <input
                        className="text-input"
                        placeholder={bankSearchPlaceholder}
                        value={bankSearch}
                        onChange={(event) => setBankSearch(event.target.value)}
                      />
                    </div>
                    <div className="field-stack">
                      <label className="field-label">{t.questionType}</label>
                      <select
                        className="text-input"
                        value={bankTypeFilter}
                        onChange={(event) => setBankTypeFilter(event.target.value)}
                      >
                        <option value="all">{allTypesLabel}</option>
                        <option value="essay">{t.essay}</option>
                        <option value="mcq">{t.mcq}</option>
                        <option value="true_false">{t.trueFalse}</option>
                        <option value="rating">{t.rating}</option>
                      </select>
                    </div>
                    <div className="field-stack">
                      <label className="field-label">{t.correctAnswer}</label>
                      <select
                        className="text-input"
                        value={bankRequiredFilter}
                        onChange={(event) => setBankRequiredFilter(event.target.value)}
                      >
                        <option value="all">{bankText.allRequiredModes}</option>
                        <option value="required">{bankText.requiredOnly}</option>
                        <option value="optional">{bankText.optionalOnly}</option>
                      </select>
                    </div>
                    <div className="field-stack">
                      <label className="field-label">{bankText.sortLabel}</label>
                      <select
                        className="text-input"
                        value={bankSort}
                        onChange={(event) => setBankSort(event.target.value)}
                      >
                        <option value="recent">{bankText.recentFirst}</option>
                        <option value="points">{bankText.pointsHighToLow}</option>
                      </select>
                    </div>
                  </div>
                  {isLoadingQuestionBank && <p className="helper-text">{bankText.loading}</p>}
                  {!isLoadingQuestionBank && !questionBank.length && <div className="empty-panel compact-panel"><p>{bankText.empty}</p></div>}
                  {!isLoadingQuestionBank && !!questionBank.length && !filteredQuestionBank.length && <div className="empty-panel compact-panel"><p>{noSearchResults}</p></div>}
                  <div className="entity-list">
                    {filteredQuestionBank.map((bankQuestion) => (
                      <div key={bankQuestion.id} className="entity-card entity-card--stack">
                        <div className="entity-main entity-main--static">
                          <strong>{bankQuestion.question_text}</strong>
                          <span>
                            {questionTypeLabel(bankQuestion.question_type)} - {bankQuestion.points} {t.points}
                            {bankQuestion.options_count ? ` - ${bankQuestion.options_count} ${bankText.optionsCountLabel}` : ""}
                          </span>
                          <span className="helper-text">
                            {Boolean(bankQuestion.is_required) ? bankText.requiredOnly : bankText.optionalOnly}
                          </span>
                          {!!bankQuestion.options?.length && (
                            <span className="helper-text">
                              {t.options}: {bankQuestion.options.map((option) => option.option_text).join(" / ")}
                            </span>
                          )}
                        </div>
                        <div className="entity-actions">
                          <button className="ghost-button" onClick={() => startEditBankQuestion(bankQuestion)}>
                            {bankText.edit}
                          </button>
                          <button className="ghost-button" disabled={!selectedAssessmentId} onClick={() => addBankQuestionToAssessment(bankQuestion.id)}>
                            {bankText.add}
                          </button>
                          <button className="danger-button" onClick={() => handleDeleteBankQuestion(bankQuestion.id)}>
                            {t.delete}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="panel" ref={contentListRef}>
        <div className="section-header">
          <div>
            <p className="eyebrow">{t.contentTreeEyebrow}</p>
            <h2 className="section-title">{t.libraryTitle}</h2>
            <p className="helper-text">{t.libraryHint}</p>
          </div>
        </div>
        {renderHierarchyLists()}
      </div>
    </section>
  );

  const renderResultsView = () => (
    <section className="dashboard-grid dashboard-grid--results">
      <div className="panel" ref={submissionsRef}>
        <div className="section-header">
          <div>
            <p className="eyebrow">{t.learnerResultsEyebrow}</p>
            <h2 className="section-title">{t.learnerResultsTitle}</h2>
          </div>
        </div>
        <div className="stack-lg">
          <div className="dashboard-card dashboard-card--hero">
            <div className="card-title-row">
              <div>
                <h3>{isArabic ? "ملخص النتائج السريع" : "Quick Results Summary"}</h3>
                <p className="helper-text">
                  {isArabic
                    ? "اختر الكورس والتقييم لعرض النتائج والجرافات والملخصات مباشرة."
                    : "Choose the course and assessment to show summaries, charts, and responses instantly."}
                </p>
              </div>
            </div>
            <div className="details-grid">
              <div className="field-stack">
                <label className="field-label">{isArabic ? "الكورس" : "Course"}</label>
                <select
                  className="text-input"
                  value={selectedCourseId || ""}
                  onChange={(event) => {
                    const nextCourseId = Number(event.target.value) || null;
                    fetchAssessments(nextCourseId);
                    fetchCourseResults(nextCourseId);
                    fetchCourseAiReport(nextCourseId);
                  }}
                >
                  <option value="">{isArabic ? "اختر كورسًا" : "Choose a course"}</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field-stack">
                <label className="field-label">{isArabic ? "التقييم" : "Assessment"}</label>
                <select
                  className="text-input"
                  value={selectedAssessmentId || ""}
                  onChange={(event) => {
                    const nextAssessmentId = Number(event.target.value) || null;
                    fetchAssessmentWorkspace(nextAssessmentId);
                  }}
                  disabled={!assessments.length}
                >
                  <option value="">{isArabic ? "اختر تقييمًا" : "Choose an assessment"}</option>
                  {assessments.map((assessment) => (
                    <option key={assessment.id} value={assessment.id}>
                      {assessment.title} - {assessment.type === "pre" ? t.pre : t.post}
                    </option>
                  ))}
                </select>
              </div>
              <div className="detail-chip">
                <span>{isArabic ? "الكورس الحالي" : "Current Course"}</span>
                <strong>{selectedCourse?.title || "-"}</strong>
              </div>
              <div className="detail-chip">
                <span>{isArabic ? "التقييم الحالي" : "Current Assessment"}</span>
                <strong>{selectedAssessment?.title || "-"}</strong>
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-title-row">
              <h3>{t.submissions}</h3>
              <div className="entity-actions">
                {selectedAssessment && <span className="helper-text">{selectedAssessment.title}</span>}
                {!!submissions.length && (
                  <button className="ghost-button" onClick={exportSubmissionsCsv}>
                    {t.exportSubmissions}
                  </button>
                )}
              </div>
            </div>
            <div className="field-stack">
              <label className="field-label">{contentSearchLabel}</label>
              <input
                className="text-input"
                placeholder={isArabic ? "ابحث باسم الطالب أو بريده أو درجته" : "Search by student name, email, or score"}
                value={resultsSearch}
                onChange={(event) => setResultsSearch(event.target.value)}
              />
            </div>
            <div className="field-stack">
              <label className="field-label">{t.submissionSort}</label>
              <select className="text-input" value={submissionSort} onChange={(event) => setSubmissionSort(event.target.value)}>
                <option value="latest">{t.latestFirst}</option>
                <option value="highest">{t.highestScore}</option>
                <option value="answers">{t.mostAnswers}</option>
              </select>
            </div>
            {!isLoadingResults && selectedAssessment && !submissions.length && <div className="empty-panel compact-panel"><p>{t.noSubmissions}</p></div>}
            {!isLoadingResults && !!submissions.length && !filteredSubmissions.length && <div className="empty-panel compact-panel"><p>{noSearchResults}</p></div>}
            {!selectedAssessment && (
              <div className="empty-panel compact-panel">
                <p>{isArabic ? "اختر تقييمًا من الملخص بالأعلى لعرض إجابات الطلاب." : "Choose an assessment from the summary above to view student responses."}</p>
              </div>
            )}
            <div className="entity-list">
              {filteredSubmissions.map((submission) => (
                <div key={submission.id} className={selectedSubmissionId === submission.id ? "entity-card entity-card--active" : "entity-card"}>
                  <button className="entity-main" onClick={() => fetchSubmissionDetails(submission.id)}>
                    <strong>{submission.full_name}</strong>
                    <span>{t.score} {submission.score} - {submission.answers_count} {t.answersCount}</span>
                    <span>{submission.email}</span>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {submissionDetails && (
            <div className="dashboard-card">
              <div className="card-title-row">
                <h3>{t.studentDetails}</h3>
                <button className="ghost-button" onClick={() => copyText(submissionDetails.submission.email, t.copiedEmail)}>{t.copyEmail}</button>
              </div>
              <div className="report-box">
                <h4>{t.aiSubmissionSummary}</h4>
                <p>{submissionDetails.submission.feedback_summary || t.noAiSummary}</p>
              </div>
              <div className="details-grid">
                <div className="detail-chip"><span>{t.name}</span><strong>{submissionDetails.submission.full_name}</strong></div>
                <div className="detail-chip"><span>{t.score}</span><strong>{submissionDetails.submission.score}</strong></div>
                <div className="detail-chip"><span>{t.governorate}</span><strong>{submissionDetails.submission.governorate || "-"}</strong></div>
                <div className="detail-chip"><span>{t.educationLevel}</span><strong>{submissionDetails.submission.education_level || "-"}</strong></div>
              </div>
              <div className="answers-list">
                {submissionDetails.answers.map((answer) => (
                  <div key={answer.id} className="answer-card">
                    <strong>{answer.question_text}</strong>
                    <p>{answer.question_type === "essay" ? answer.answer_text || t.noWrittenAnswer : answer.selected_option_text || t.noSelectedOption}</p>
                    {(answer.sentiment_label || answer.detected_skills) && (
                      <div className="ai-meta">
                        <span>{t.sentiment}: {answer.sentiment_label || "-"}</span>
                        <span>{t.level}: {answer.detected_skills || "-"}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="panel" ref={analyticsRef}>
        <div className="section-header">
          <div>
            <p className="eyebrow">{t.dashboardEyebrow}</p>
            <h2 className="section-title">{t.dashboardTitle}</h2>
          </div>
        </div>
        <div className="stack-lg">
          {courseResults && (
            <div className="dashboard-card">
              <div className="card-title-row">
                <h3>{t.courseSummary}</h3>
                <span className="helper-text">{courseResults.course.title}</span>
              </div>
              <div className="details-grid">
                <div className="detail-chip"><span>{t.totalStudents}</span><strong>{courseResults.summary.total_students}</strong></div>
                <div className="detail-chip"><span>{t.totalSubmissions}</span><strong>{courseResults.summary.total_submissions}</strong></div>
                <div className="detail-chip"><span>{t.averageScore}</span><strong>{courseResults.summary.average_score}</strong></div>
                <div className="detail-chip"><span>{t.improvedStudents}</span><strong>{courseResults.summary.improved_students}/{courseResults.summary.compared_students}</strong></div>
              </div>
            </div>
          )}

          {courseAiReport?.report && (
            <div className="dashboard-card">
              <div className="card-title-row">
                <h3>{t.aiCourseReport}</h3>
                <div className="entity-actions">
                  <button className="ghost-button" onClick={exportCourseAiReportPdf}>{pdfText.exportPdf}</button>
                  <button className="ghost-button" onClick={copyCourseAiReport}>{t.copyCourseReport}</button>
                  <button className="ghost-button" onClick={exportCourseAiReportText}>{t.exportAiCourseReport}</button>
                </div>
              </div>
              <div className="report-box">
                <h4>{courseAiReport.report.headline}</h4>
                <p>{courseAiReport.report.summary}</p>
                {!!courseAiReport.report.strengths?.length && <div><strong>{t.strengths}</strong><ul className="flat-list">{courseAiReport.report.strengths.map((item, index) => <li key={index}>{item}</li>)}</ul></div>}
                {!!courseAiReport.report.risks?.length && <div><strong>{t.risks}</strong><ul className="flat-list">{courseAiReport.report.risks.map((item, index) => <li key={index}>{item}</li>)}</ul></div>}
                {!!courseAiReport.report.recommendations?.length && <div><strong>{t.recommendations}</strong><ul className="flat-list">{courseAiReport.report.recommendations.map((item, index) => <li key={index}>{item}</li>)}</ul></div>}
              </div>
            </div>
          )}

          {assessmentAnalytics && (
            <div className="dashboard-card">
              <div className="card-title-row">
                <h3>{t.assessmentSummary}</h3>
                <span className="helper-text">{assessmentAnalytics.assessment.title}</span>
              </div>
              <div className="details-grid">
                <div className="detail-chip"><span>{t.totalSubmissions}</span><strong>{assessmentAnalytics.analytics.total_submissions}</strong></div>
                <div className="detail-chip"><span>{t.averageScore}</span><strong>{assessmentAnalytics.analytics.average_score}</strong></div>
                <div className="detail-chip"><span>{t.totalAiInsights}</span><strong>{assessmentAnalytics.analytics.total_ai_insights}</strong></div>
              </div>
            </div>
          )}

          {assessmentAiReport?.report && (
            <div className="dashboard-card">
              <div className="card-title-row">
                <h3>{t.aiAssessmentReport}</h3>
                <div className="entity-actions">
                  <button className="ghost-button" onClick={exportAssessmentAiReportPdf}>{pdfText.exportPdf}</button>
                  <button className="ghost-button" onClick={copyAssessmentAiReport}>{t.copyAssessmentReport}</button>
                  <button className="ghost-button" onClick={exportAssessmentAiReportText}>{t.exportAiAssessmentReport}</button>
                </div>
              </div>
              <div className="report-box">
                <h4>{assessmentAiReport.report.headline}</h4>
                <p>{assessmentAiReport.report.summary}</p>
                <div className="details-grid">
                  <div className="detail-chip"><span>{t.dominantSentiment}</span><strong>{assessmentAiReport.report.dominant_sentiment}</strong></div>
                  <div className="detail-chip"><span>{t.dominantLevel}</span><strong>{assessmentAiReport.report.dominant_level}</strong></div>
                </div>
                {!!assessmentAiReport.report.top_keywords?.length && <p>{t.topKeywords}: {assessmentAiReport.report.top_keywords.join(", ")}</p>}
                {!!assessmentAiReport.report.recommendations?.length && <ul className="flat-list">{assessmentAiReport.report.recommendations.map((item, index) => <li key={index}>{item}</li>)}</ul>}
              </div>
            </div>
          )}

          {!!improvementChartData.length && <div className="dashboard-card"><h3>{t.prePostComparison}</h3><div className="chart-panel"><ResponsiveContainer width="100%" height={280}><BarChart data={improvementChartData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" hide /><YAxis /><Tooltip /><Legend /><Bar dataKey="improvement" fill="#2f855a" /></BarChart></ResponsiveContainer></div></div>}
          {assessmentAnalytics && <div className="dashboard-card"><h3>{t.sentimentLevels}</h3><div className="results-charts-grid"><div className="chart-panel"><ResponsiveContainer width="100%" height={260}><PieChart><Pie data={sentimentChartData} dataKey="value" outerRadius={90} fill="#2b6c8f" label /><Tooltip /></PieChart></ResponsiveContainer></div><div className="chart-panel"><ResponsiveContainer width="100%" height={260}><BarChart data={levelChartData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis /><Tooltip /><Bar dataKey="value" fill="#1f4a6e" /></BarChart></ResponsiveContainer></div></div></div>}
          {courseResults?.students?.length ? (
            <div className="dashboard-card">
              <div className="card-title-row">
                <h3>{t.courseStudentResults}</h3>
                <button className="ghost-button" onClick={exportCourseResultsCsv}>{t.exportCourseResults}</button>
              </div>
              <div className="field-stack">
                <label className="field-label">{contentSearchLabel}</label>
                <input
                  className="text-input"
                  placeholder={isArabic ? "ابحث في نتائج طلاب الكورس" : "Search course student results"}
                  value={resultsSearch}
                  onChange={(event) => setResultsSearch(event.target.value)}
                />
              </div>
              <div className="field-stack">
                <label className="field-label">{t.courseResultSort}</label>
                <select className="text-input" value={courseResultsSort} onChange={(event) => setCourseResultsSort(event.target.value)}>
                  <option value="name">{t.nameSort}</option>
                  <option value="improvement">{t.bestImprovement}</option>
                  <option value="post">{t.highestPost}</option>
                </select>
              </div>
              {!filteredCourseStudents.length && <div className="empty-panel compact-panel"><p>{noSearchResults}</p></div>}
              {!!filteredCourseStudents.length && (
                <div className="table-wrap">
                  <table className="results-table">
                    <thead>
                      <tr><th>{t.student}</th><th>{t.pre}</th><th>{t.post}</th><th>{t.improvement}</th></tr>
                    </thead>
                    <tbody>
                      {filteredCourseStudents.map((student) => (
                        <tr key={student.student_id}>
                          <td>{student.full_name}</td>
                          <td>{student.pre_score ?? "-"}</td>
                          <td>{student.post_score ?? "-"}</td>
                          <td>{student.improvement ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );

  return (
    <div className="stack-lg">
      {feedback && <div className={`feedback-banner feedback-banner--${feedbackType}`}>{feedback}</div>}
      <div className="panel subnav-panel">
        <div className="section-header">
          <div>
            <p className="eyebrow">{isArabic ? "لوحة النتائج" : "Results Dashboard"}</p>
            <h2 className="section-title">
              {isArabic ? "ملخص المدرب والجرافات والنتائج" : "Trainer summary, charts, and results"}
            </h2>
          </div>
        </div>
        <div className="quick-jump">
          <span className="quick-jump__label">{t.jumpTo}</span>
          <button className="quick-jump__button" onClick={() => scrollToSection(submissionsRef)}>{t.submissionsSection}</button>
          <button className="quick-jump__button" onClick={() => scrollToSection(analyticsRef)}>{t.analyticsSection}</button>
        </div>
      </div>
      {activeTab === "management" ? renderManagementView() : renderResultsView()}
      {confirmState && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>{confirmState.title}</h3>
            <p>{confirmState.message}</p>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setConfirmState(null)}>
                {isArabic ? "إلغاء" : "Cancel"}
              </button>
              <button className="danger-button" onClick={confirmState.action}>
                {isArabic ? "تأكيد الحذف" : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrainerResultsPage;

